/**
 * Thin fetch wrapper that attaches the session bearer token to /api/* calls.
 *
 * Session ids are namespaced by sui_address so multiple accounts on the same
 * browser profile don't overwrite each other in localStorage. The "current"
 * session is whichever address is in coffee_rewards_address (i.e. the
 * currently-logged-in account).
 */
const LEGACY_SESSION_KEY = 'coffee_rewards_session_id';
const ADDRESS_KEY = 'coffee_rewards_address';

function sessionKey(addr: string | null): string {
  return addr ? `coffee_rewards_session_id:${addr}` : LEGACY_SESSION_KEY;
}

function currentAddress(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(ADDRESS_KEY);
}

export function getSessionId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const addr = currentAddress();
  // Prefer the per-address key; fall back to legacy global key for old sessions.
  return localStorage.getItem(sessionKey(addr)) || localStorage.getItem(LEGACY_SESSION_KEY);
}

export function setSessionId(id: string, address?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  const addr = address ?? currentAddress();
  if (addr) localStorage.setItem(sessionKey(addr), id);
  // Mirror to the legacy key for backwards-compat code paths.
  localStorage.setItem(LEGACY_SESSION_KEY, id);
}

export function clearSessionId(): void {
  if (typeof localStorage === 'undefined') return;
  // Clear all per-address session keys, plus the legacy one.
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const k = localStorage.key(i);
    if (k && (k === LEGACY_SESSION_KEY || k.startsWith('coffee_rewards_session_id:'))) {
      localStorage.removeItem(k);
    }
  }
}

export class ApiError extends Error {
  // `body` is the parsed JSON error payload (e.g. `{ error, reason }`), when
  // the response had one — every server 4xx/5xx handler in api/arcade.ts
  // (and elsewhere) sends structured fields beyond just `error`, and until
  // this existed callers had no way to read them: they could only see the
  // `message` string, so a caller like ArcadePanel that wanted to branch on
  // `reason` had no real field to read it from.
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

export async function apiFetch<T>(path: string, body?: unknown): Promise<T> {
  const sessionId = getSessionId();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (sessionId) headers['Authorization'] = `Bearer ${sessionId}`;

  const res = await fetch(path, {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let errorBody: unknown;
    try {
      const data = await res.json();
      errorBody = data;
      if (data?.error) message = data.error;
    } catch { /* ignore */ }
    throw new ApiError(res.status, message, errorBody);
  }

  return res.json() as Promise<T>;
}
